import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import type { LoginResponse } from '@bidmanager/shared';
import { errorMessage, request } from '../api';

interface InvitationPreview {
  email: string;
  clientName: string;
  profileName: string;
  expiresAt: string;
}

/**
 * Invitation redemption (§3.1) — the page the invite link points at.
 *
 * Public by necessity: the whole point is that the visitor has no account yet.
 * The email is shown but not editable — it was fixed at invite time and the
 * server ignores anything else. On success the bidder is sent to the desktop
 * app rather than into this portal: the portal is the client's surface, and a
 * BIDDER session here would 403 on every page.
 */
export function AcceptInvite(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const preview = useQuery({
    queryKey: ['invite-preview', token],
    queryFn: () =>
      request<InvitationPreview>('GET', `/invitations/preview?token=${encodeURIComponent(token ?? '')}`),
    enabled: Boolean(token),
    retry: false,
  });

  const accept = useMutation({
    mutationFn: () =>
      request<LoginResponse>('POST', '/auth/accept-invitation', { token, username, password }),
    onSuccess: () => {
      setDone(true);
      setError(null);
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  // There is no password reset flow, so a typo here locks the account out for
  // good. The confirm field is cheaper than the support conversation.
  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < 12;

  if (preview.isLoading) {
    return <div className="card login muted">Checking your invitation…</div>;
  }

  if (preview.isError || !preview.data) {
    return (
      <div className="card login">
        <h1 style={{ marginTop: 0, fontSize: 19 }}>BidManager</h1>
        <p className="error" style={{ marginBottom: 4 }}>
          This invitation link is not valid.
        </p>
        <p className="muted small" style={{ marginBottom: 0 }}>
          It may have expired, been revoked, or already been used. Ask the person who invited you
          to send a new link.
        </p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="card login">
        <h1 style={{ marginTop: 0, fontSize: 19 }}>Account created</h1>
        <p className="small" style={{ marginTop: 0 }}>
          You can now sign in to the <strong>BidManager desktop app</strong> as{' '}
          <strong>{preview.data.email}</strong> with the password you just set.
        </p>
        <p className="muted small">
          Bidding happens in the desktop app, not in this browser portal.
        </p>
        <Link
          className="button primary"
          to="/download"
          style={{ display: 'block', textAlign: 'center' }}
        >
          Download the desktop app
        </Link>
      </div>
    );
  }

  return (
    <form
      className="card login"
      onSubmit={(event) => {
        event.preventDefault();
        if (!mismatch && !tooShort) accept.mutate();
      }}
    >
      <h1 style={{ marginTop: 0, fontSize: 19 }}>Join {preview.data.clientName}</h1>
      <p className="muted small" style={{ marginTop: 0 }}>
        You've been invited to bid using the profile <strong>{preview.data.profileName}</strong>.
        Choose a username and password to finish setting up your account.
      </p>

      <div className="field">
        <label>Email — fixed by the invitation</label>
        <input value={preview.data.email} disabled />
      </div>
      <div className="field">
        <label htmlFor="username">Username</label>
        <input
          id="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
          required
          minLength={3}
          maxLength={50}
        />
      </div>
      <div className="field">
        <label htmlFor="password">Password (at least 12 characters)</label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={12}
        />
        {tooShort && <span className="warn small">Use at least 12 characters.</span>}
      </div>
      <div className="field">
        <label htmlFor="confirm">Confirm password</label>
        <input
          id="confirm"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          required
        />
        {mismatch && <span className="warn small">Passwords don't match.</span>}
      </div>

      {error && <p className="error small">{error}</p>}

      <button
        className="primary"
        type="submit"
        disabled={!username.trim() || !password || mismatch || tooShort || accept.isPending}
        style={{ width: '100%' }}
      >
        {accept.isPending ? 'Creating account…' : 'Create account'}
      </button>

      <p className="muted small" style={{ margin: '10px 0 0' }}>
        This invitation expires {new Date(preview.data.expiresAt).toLocaleDateString()}.
      </p>
    </form>
  );
}
